import { useEffect, useRef } from 'react';

import { getSocketInstance } from '~/libs/socket';
import type { ThinkingMessage } from './types';

type UseChatSocketProps = {
  chatId: string;
  onThinking: (data: ThinkingMessage) => void;
  onAssistant: (data: ThinkingMessage) => void;
};

export function useChatSocket({
  chatId,
  onThinking,
  onAssistant,
}: UseChatSocketProps) {
  const socketRef = useRef<ReturnType<typeof getSocketInstance> | null>(null);

  useEffect(() => {
    const socket = getSocketInstance();
    socketRef.current = socket;

    function handleChatThinking(data: ThinkingMessage) {
      if (data.chat_id !== chatId) return;
      onThinking(data);
    }

    function handleChatAssistant(data: ThinkingMessage) {
      if (data.chat_id !== chatId || !data.message.content) return;
      onAssistant(data);
    }

    socket.on('chat.thinking', handleChatThinking);
    socket.on('chat.assistant', handleChatAssistant);
    socket.connect();

    return () => {
      socket.off('chat.thinking', handleChatThinking);
      socket.off('chat.assistant', handleChatAssistant);
    };
  }, [chatId, onThinking, onAssistant]);

  return {
    socket: socketRef.current,
  };
}
